import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import i18n from 'i18next';
import { api, onSessionChange, refreshSession, request, tokens, type AuthPayload } from '@/api/client';
import type { Preferences, Session } from '@/api/types';
import { changeLocale, storedLocale } from '@/i18n';
import { useTheme } from '@/theme/ThemeProvider';

type Status = 'loading' | 'anonymous' | 'authenticated';

interface AuthContextValue {
  status: Status;
  session: Session | null;
  can: (permission: string) => boolean;
  canAny: (...permissions: string[]) => boolean;
  hasRole: (role: string) => boolean;
  login: (username: string, password: string, hospitalId?: string) => Promise<void>;
  logout: () => Promise<void>;
  switchHospital: (hospitalId: string) => Promise<void>;
  savePreferences: (patch: Partial<Preferences>) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function toSession(payload: AuthPayload): Session {
  const { accessToken: _a, refreshToken: _r, expiresIn: _e, tokenType: _t, ...rest } = payload;
  return rest as unknown as Session;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { setAppearance } = useTheme();
  const [status, setStatus] = useState<Status>('loading');
  const [session, setSession] = useState<Session | null>(null);
  const preferencesApplied = useRef(false);

  // Apply account preferences once per sign-in (not on every token refresh).
  const applyPreferences = useCallback(
    async (s: Session) => {
      if (preferencesApplied.current) return;
      preferencesApplied.current = true;
      const p = s.preferences;
      if (p) setAppearance({ theme: p.theme || 'system', accent: p.accent, density: p.density ?? 'comfortable' });
      const locale = p?.locale || storedLocale() || s.hospital?.defaultLocale || i18n.language;
      await changeLocale(locale, true);
    },
    [setAppearance],
  );

  useEffect(() => {
    onSessionChange((payload) => {
      if (!payload) {
        preferencesApplied.current = false;
        setSession(null);
        setStatus('anonymous');
        queryClient.clear();
        return;
      }
      const s = toSession(payload);
      setSession(s);
      setStatus('authenticated');
      void applyPreferences(s);
    });
    if (tokens.refresh) {
      void refreshSession().then((ok) => {
        if (!ok && !tokens.access) setStatus('anonymous');
      });
    } else {
      setStatus('anonymous');
    }
  }, [applyPreferences, queryClient]);

  const login = useCallback(
    async (username: string, password: string, hospitalId?: string) => {
      const payload = await request<AuthPayload>('POST', '/api/v1/auth/login', {
        body: hospitalId ? { username, password, hospitalId } : { username, password },
        auth: false,
      });
      tokens.set(payload);
      preferencesApplied.current = false;
      const s = toSession(payload);
      setSession(s);
      setStatus('authenticated');
      await applyPreferences(s);
    },
    [applyPreferences],
  );

  const logout = useCallback(async () => {
    const refresh = tokens.refresh;
    if (refresh) await request('POST', '/api/v1/auth/logout', { body: { refreshToken: refresh }, auth: false }).catch(() => undefined);
    tokens.set(null);
    preferencesApplied.current = false;
    setSession(null);
    setStatus('anonymous');
    queryClient.clear();
  }, [queryClient]);

  const switchHospital = useCallback(
    async (hospitalId: string) => {
      const payload = await request<AuthPayload>('POST', '/api/v1/auth/switch-hospital', {
        body: { refreshToken: tokens.refresh, hospitalId },
        auth: false,
      });
      tokens.set(payload);
      setSession(toSession(payload));
      queryClient.clear();
      await changeLocale(i18n.language, true);
    },
    [queryClient],
  );

  const savePreferences = useCallback(async (patch: Partial<Preferences>) => {
    const prefs = await api.put<Preferences>('/api/v1/me/preferences', patch);
    setSession((s) => (s ? { ...s, preferences: prefs } : s));
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const perms = new Set(session?.permissions ?? []);
    return {
      status,
      session,
      can: (p) => perms.has(p),
      canAny: (...ps) => ps.some((p) => perms.has(p)),
      hasRole: (r) => (session?.roles ?? []).includes(r),
      login,
      logout,
      switchHospital,
      savePreferences,
    };
  }, [status, session, login, logout, switchHospital, savePreferences]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
